import { format, getMonth, getYear } from 'date-fns'
import {
  Box,
  Card,
  Skeleton,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { routes } from '@/router'
import { useReadCompanyRevenue } from '@/api/accounting-dealer'
import { RevenueChart, RouterLink } from '@/components'
import { useCompany } from '@/context/companies'
import { capitalize, formatCurrency } from '@/helpers'
import { ErrorOutline } from '@mui/icons-material'
import { grey } from '@mui/material/colors'

const CURRENT_YEAR = getYear(new Date())
const CURRENT_MONTH = getMonth(new Date()) + 1

export function Revenue() {
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'))

  const { company } = useCompany()

  const { data: revenue, isLoading } = useReadCompanyRevenue({
    company_key: company.key,
    year: CURRENT_YEAR,
  })

  if (isLoading) {
    return (
      <Skeleton variant="rounded" height={isMobile ? 140 : 230} width="100%" />
    )
  }

  if (!revenue) {
    return (
      <Card
        sx={{
          height: 135,
          bgcolor: grey[200],
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <ErrorOutline color="error" fontSize="large" />
        <Typography color="gray">
          Não foi possível carregar o faturamento.
        </Typography>
      </Card>
    )
  }

  const currentMonthAndYear = format(new Date(), 'MMMM/yyyy')

  const currentMonthRevenue =
    revenue.find(
      (item) => item.year === CURRENT_YEAR && item.month === CURRENT_MONTH,
    )?.revenue || 0

  if (isMobile) {
    return (
      <RouterLink to={routes.revenue}>
        <Card
          sx={{
            bgcolor: 'primary.light',
            color: 'white',
            '&:hover': { boxShadow: 3 },
          }}
        >
          <Typography fontSize={18} mb={2}>
            Faturamento
          </Typography>
          <Typography variant="h2" fontSize={40} textAlign="center">
            <Typography component="span" variant="h3">
              R$
            </Typography>{' '}
            {formatCurrency(currentMonthRevenue, { style: 'decimal' })}
          </Typography>
          <Typography variant="body2" textAlign="center">
            {capitalize(currentMonthAndYear)}
          </Typography>
        </Card>
      </RouterLink>
    )
  }

  return (
    <Card
      sx={{
        bgcolor: 'primary.light',
        color: 'white',
      }}
    >
      <Typography fontSize={20} mb={2}>
        Faturamento
      </Typography>

      <Box display="flex" gap={10} justifyContent="space-between">
        <Box width="40%">
          <Typography variant="h2" fontSize={48}>
            <Typography component="span" variant="h3">
              R$
            </Typography>{' '}
            {formatCurrency(currentMonthRevenue, { style: 'decimal' })}
          </Typography>

          <Typography variant="body2">
            {capitalize(currentMonthAndYear)}
          </Typography>
        </Box>

        <Box width="60%">
          <RevenueChart
            sx={{
              '.MuiChartsAxis-tickLabel': {
                fill: '#fff !important',
              },
            }}
            height={160}
            year={CURRENT_YEAR}
          />
        </Box>
      </Box>
    </Card>
  )
}
